import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTrip } from '../lib/tripContext.jsx';
import { useSession } from '../lib/sessionContext.jsx';
import { useTestMode } from '../lib/testModeContext.jsx';
import { useTripJson } from '../hooks/data.js';
import { fetchMedical, saveMedical, broadcastSOS } from '../lib/sosApi.js';

const CATEGORY_GLYPH = {
  primary: '⚠',
  medical: '+',
  police: '★',
  marine: '~',
  fire: '⚒',
};

export default function SOS() {
  const { tripId, trip } = useTrip();
  const session = useSession();
  const testMode = useTestMode();
  const data = useTripJson(tripId, 'sos.json');

  const [coords, setCoords] = useState(null);
  const [coordsError, setCoordsError] = useState(null);
  const [coordsLoading, setCoordsLoading] = useState(false);

  const [medical, setMedical] = useState(null);
  const [editingMedical, setEditingMedical] = useState(false);
  const [medicalDraft, setMedicalDraft] = useState({});
  const [medicalSaving, setMedicalSaving] = useState(false);

  const [broadcasting, setBroadcasting] = useState(false);
  const [broadcastResult, setBroadcastResult] = useState(null);

  const [error, setError] = useState(null);

  const meId = session?.role === 'player' ? session.recipient_id : null;
  const me = (trip?.players || []).find((p) => p.id === meId) || null;

  const refreshLocation = useCallback(() => {
    // Test mode: short-circuit to the configured mock location so a
    // Portland test session can broadcast a Mendenhall coordinate
    // without touching real GPS or asking for permission.
    if (testMode.active && testMode.mockLocation) {
      setCoords({
        lat: testMode.mockLocation.lat,
        lng: testMode.mockLocation.lng,
        accuracy: 5,
        when: new Date().toISOString(),
        mock: true,
      });
      setCoordsError(null);
      setCoordsLoading(false);
      return;
    }
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setCoordsError('Geolocation not supported on this device.');
      return;
    }
    setCoordsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          when: new Date(pos.timestamp).toISOString(),
        });
        setCoordsError(null);
        setCoordsLoading(false);
      },
      (err) => {
        setCoordsError(err.message || 'Could not read location.');
        setCoordsLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
    );
  }, [testMode.active, testMode.mockLocation]);

  useEffect(() => {
    refreshLocation();
  }, [refreshLocation]);

  const refreshMedical = useCallback(async () => {
    if (!meId) return;
    try {
      const res = await fetchMedical({ tripId });
      setMedical(res.medical || null);
      setMedicalDraft(res.medical || {});
    } catch (err) {
      if (err.status === 'api_unavailable') return;
      if (err.status === 401) return;
      setError(err.message || String(err));
    }
  }, [tripId, meId]);

  useEffect(() => {
    if (!session.loading) refreshMedical();
  }, [session.loading, refreshMedical]);

  const onSaveMedical = useCallback(async () => {
    setMedicalSaving(true);
    setError(null);
    try {
      const res = await saveMedical({ tripId, fields: medicalDraft });
      setMedical(res.medical || null);
      setEditingMedical(false);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setMedicalSaving(false);
    }
  }, [tripId, medicalDraft]);

  const onBroadcast = useCallback(async () => {
    if (!coords) {
      setError('Get your location first — tap "refresh GPS" above.');
      return;
    }
    if (
      !window.confirm(
        'Send SOS to the rest of the trip group? They\'ll get a push notification with your coordinates.'
      )
    ) {
      return;
    }
    setBroadcasting(true);
    setError(null);
    try {
      const res = await broadcastSOS({
        tripId,
        lat: coords.lat,
        lng: coords.lng,
        accuracy: coords.accuracy,
        includeMedical: true,
      });
      setBroadcastResult(res);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBroadcasting(false);
    }
  }, [tripId, coords]);

  const sosData = data.data;
  const tiers = sosData?.tiers || [];

  const mapsUrl = useMemo(() => {
    if (!coords) return null;
    return `https://www.google.com/maps/search/?api=1&query=${coords.lat},${coords.lng}`;
  }, [coords]);

  const broadcastTemplate = sosData?.broadcast_template;

  return (
    <div className="mx-auto w-full max-w-screen-sm space-y-4 p-4">
      <header>
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fg-tertiary">
          emergency · always available
        </p>
        <h1 className="mt-1 font-serif text-2xl text-danger">SOS</h1>
        <p className="mt-1 text-sm text-fg-secondary">
          Numbers below work offline once cached. Group SOS broadcast and medical sync require
          a network connection.
        </p>
      </header>

      <CallTile
        primary
        name="911 — emergency"
        phone="911"
        phoneDisplay="911"
        context="Police, fire, ambulance, wilderness rescue. The State Troopers wilderness desk is reached via 911."
        glyph="⚠"
      />

      <section
        aria-labelledby="my-location"
        className="card overflow-hidden"
      >
        <header className="flex items-baseline justify-between gap-2 border-b border-border-tertiary bg-bg-secondary px-4 py-3">
          <h2
            id="my-location"
            className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary"
          >
            your location
          </h2>
          <button
            type="button"
            onClick={refreshLocation}
            disabled={coordsLoading}
            className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary underline-offset-4 hover:underline disabled:opacity-50"
          >
            {coordsLoading ? '…locating' : 'refresh GPS'}
          </button>
        </header>
        <div className="px-4 py-3">
          {coords ? (
            <>
              <p className="font-mono text-lg leading-tight text-fg-primary">
                {coords.lat.toFixed(5)}, {coords.lng.toFixed(5)}
              </p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
                ±{Math.round(coords.accuracy)} m · captured {new Date(coords.when).toLocaleTimeString()}
              </p>
              {mapsUrl && (
                <a
                  href={mapsUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="mt-2 inline-block font-mono text-[11px] uppercase tracking-wider text-action underline-offset-4 hover:underline"
                >
                  open in maps →
                </a>
              )}
            </>
          ) : (
            <p className="text-sm text-fg-secondary">
              {coordsError || '…locating'}
            </p>
          )}
        </div>
      </section>

      {meId && (
        <section
          aria-labelledby="group-sos"
          className="card overflow-hidden border-danger/40"
        >
          <header className="border-b border-danger/40 bg-danger/10 px-4 py-3">
            <h2
              id="group-sos"
              className="font-mono text-[10px] uppercase tracking-wider text-danger"
            >
              group SOS broadcast
            </h2>
            <p className="mt-1 text-sm text-fg-primary">
              Force-pushes your name, coordinates, and medical info to the other players + Anar.
              Bypasses quiet hours.
            </p>
          </header>
          <div className="px-4 py-3">
            <button
              type="button"
              onClick={onBroadcast}
              disabled={broadcasting || !coords}
              className="w-full rounded-md border border-danger bg-danger px-3 py-3 font-mono text-sm uppercase tracking-wider text-action-fg transition disabled:opacity-50"
            >
              {broadcasting ? 'broadcasting…' : 'BROADCAST SOS TO GROUP'}
            </button>
            {broadcastResult && (
              <p className="mt-2 rounded-md border border-success/40 bg-success/10 p-2 font-mono text-[10px] uppercase tracking-wider text-success">
                sent · push to{' '}
                {(broadcastResult.push_results || []).reduce((n, r) => n + (r.sent || 0), 0)} subscribers
              </p>
            )}
            {coords && broadcastTemplate?.sms_template && (
              <p className="mt-2 font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
                fallback SMS:{' '}
                <a
                  href={`sms:?body=${encodeURIComponent(
                    fillTemplate(broadcastTemplate.sms_template, {
                      lat: coords.lat,
                      lng: coords.lng,
                      sender_name: me?.display_name || me?.name || 'me',
                    })
                  )}`}
                  className="normal-case tracking-normal text-action"
                >
                  open SMS pre-filled →
                </a>
              </p>
            )}
          </div>
        </section>
      )}

      <section aria-labelledby="tiers" className="space-y-2">
        <h2
          id="tiers"
          className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary"
        >
          contacts (tap to call · works offline)
        </h2>
        {tiers
          .filter((t) => !t.always_first || t.tier > 1)
          .map((t) => (
            <CallTile
              key={t.id}
              name={t.name}
              phone={t.phone}
              phoneDisplay={t.phone_display}
              context={t.context}
              address={t.address}
              lat={t.lat}
              lng={t.lng}
              glyph={CATEGORY_GLYPH[t.category]}
            />
          ))}
        {data.loading && (
          <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
            …loading contacts
          </p>
        )}
      </section>

      {meId && (
        <section
          aria-labelledby="medical"
          className="card overflow-hidden"
        >
          <header className="flex items-baseline justify-between gap-2 border-b border-border-tertiary bg-bg-secondary px-4 py-3">
            <h2
              id="medical"
              className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary"
            >
              your medical info · only you + Anar see this
            </h2>
            <button
              type="button"
              onClick={() => {
                if (editingMedical) {
                  setEditingMedical(false);
                  setMedicalDraft(medical || {});
                } else {
                  setEditingMedical(true);
                }
              }}
              className="font-mono text-[10px] uppercase tracking-wider text-action underline-offset-4 hover:underline"
            >
              {editingMedical ? 'cancel' : medical ? 'edit' : 'add'}
            </button>
          </header>
          <div className="px-4 py-3 space-y-3">
            {editingMedical ? (
              <MedicalForm
                draft={medicalDraft}
                onChange={setMedicalDraft}
                onSave={onSaveMedical}
                saving={medicalSaving}
              />
            ) : (
              <MedicalDisplay record={medical} />
            )}
          </div>
        </section>
      )}

      {sosData?.bear_safety && <BearSafetyCard doc={sosData.bear_safety} />}

      {(sosData?.tide_safety?.length > 0 || sosData?.weather_safety?.length > 0) && (
        <section className="card overflow-hidden">
          <header className="border-b border-border-tertiary bg-bg-secondary px-4 py-3">
            <h2 className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
              other hazards
            </h2>
          </header>
          <ul className="space-y-2 px-4 py-3 text-sm text-fg-secondary">
            {(sosData.weather_safety || []).map((x) => (
              <li key={x} className="flex gap-2">
                <span className="text-fg-tertiary" aria-hidden>
                  •
                </span>
                <span>{x}</span>
              </li>
            ))}
            {(sosData.tide_safety || []).map((x) => (
              <li key={x} className="flex gap-2">
                <span className="text-fg-tertiary" aria-hidden>
                  •
                </span>
                <span>{x}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {error && (
        <div className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 font-mono text-[11px] text-danger">
          {error}
        </div>
      )}
    </div>
  );
}

function CallTile({ name, phone, phoneDisplay, context, address, lat, lng, glyph, primary }) {
  const tel = `tel:${phone}`;
  const directions =
    typeof lat === 'number' && typeof lng === 'number'
      ? `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`
      : null;
  return (
    <div
      className={[
        'card overflow-hidden',
        primary ? 'border-danger/60 bg-danger/10' : '',
      ].join(' ')}
    >
      <a
        href={tel}
        className={[
          'flex items-center gap-3 px-4 py-3 transition',
          primary ? 'hover:bg-danger/15' : 'hover:bg-bg-tertiary',
        ].join(' ')}
      >
        <span
          aria-hidden
          className={[
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-mono text-base',
            primary
              ? 'bg-danger text-action-fg'
              : 'bg-bg-secondary text-fg-secondary',
          ].join(' ')}
        >
          {glyph || '•'}
        </span>
        <div className="min-w-0 flex-1">
          <p
            className={[
              'font-medium leading-tight',
              primary ? 'text-danger' : 'text-fg-primary',
            ].join(' ')}
          >
            {name}
          </p>
          <p className="font-mono text-base">{phoneDisplay}</p>
          {address && (
            <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
              {address}
            </p>
          )}
          {context && <p className="mt-1 text-xs text-fg-secondary">{context}</p>}
        </div>
      </a>
      {directions && (
        <a
          href={directions}
          target="_blank"
          rel="noreferrer noopener"
          className="block border-t border-border-tertiary bg-bg-secondary px-4 py-2 text-center font-mono text-[10px] uppercase tracking-wider text-action transition hover:bg-bg-tertiary"
        >
          directions →
        </a>
      )}
    </div>
  );
}

function MedicalDisplay({ record }) {
  if (!record) {
    return (
      <p className="font-serif italic text-sm text-fg-secondary">
        No medical info on file. Add allergies, blood type, current meds, and an emergency
        contact so the group has it if you can&apos;t answer.
      </p>
    );
  }
  const fields = [
    ['Full name', record.full_name],
    ['DOB', record.date_of_birth],
    ['Blood type', record.blood_type],
    ['Allergies', record.allergies],
    ['Medications', record.medications],
    ['Conditions', record.conditions],
    ['Insurance', record.insurance_info],
    ['Your phone', record.own_phone],
    [
      'Emergency contact',
      [record.emergency_contact_name, record.emergency_contact_relationship]
        .filter(Boolean)
        .join(' · '),
    ],
    ['Contact phone', record.emergency_contact_phone],
  ];
  return (
    <dl className="space-y-1.5 text-sm">
      {fields
        .filter(([, v]) => v)
        .map(([label, value]) => (
          <div key={label} className="flex flex-wrap gap-x-2">
            <dt className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary min-w-[80px]">
              {label}
            </dt>
            <dd className="text-fg-primary">{value}</dd>
          </div>
        ))}
    </dl>
  );
}

function MedicalForm({ draft, onChange, onSave, saving }) {
  const fields = [
    { name: 'full_name', label: 'Full legal name', placeholder: 'Last, First' },
    { name: 'date_of_birth', label: 'Date of birth', placeholder: '1990-04-23' },
    {
      name: 'blood_type',
      label: 'Blood type',
      placeholder: '',
      type: 'select',
      options: ['', 'A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-', 'unknown'],
    },
    { name: 'allergies', label: 'Allergies', placeholder: 'Medications + foods', type: 'textarea' },
    { name: 'medications', label: 'Current medications', placeholder: 'Name + dose', type: 'textarea' },
    { name: 'conditions', label: 'Conditions', placeholder: 'Anything responders should know', type: 'textarea' },
    { name: 'insurance_info', label: 'Insurance (provider + member ID)', placeholder: '' },
    { name: 'own_phone', label: 'Your phone', placeholder: '+1…' },
    { name: 'emergency_contact_name', label: 'Emergency contact name', placeholder: '' },
    { name: 'emergency_contact_phone', label: 'Emergency contact phone', placeholder: '+1…' },
    { name: 'emergency_contact_relationship', label: 'Relationship', placeholder: 'Spouse, parent…' },
  ];
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave();
      }}
      className="space-y-2"
    >
      {fields.map((f) => (
        <label key={f.name} className="block">
          <span className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
            {f.label}
          </span>
          {f.type === 'textarea' ? (
            <textarea
              value={draft[f.name] || ''}
              onChange={(e) => onChange({ ...draft, [f.name]: e.target.value })}
              rows={2}
              className="mt-1 w-full rounded-md border border-border-tertiary bg-bg-primary px-3 py-1.5 text-sm focus:border-action focus:outline-none"
              placeholder={f.placeholder}
            />
          ) : f.type === 'select' ? (
            <select
              value={draft[f.name] || ''}
              onChange={(e) => onChange({ ...draft, [f.name]: e.target.value })}
              className="mt-1 w-full rounded-md border border-border-tertiary bg-bg-primary px-3 py-1.5 text-sm focus:border-action focus:outline-none"
            >
              {f.options.map((o) => (
                <option key={o} value={o}>
                  {o || '— select —'}
                </option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              value={draft[f.name] || ''}
              onChange={(e) => onChange({ ...draft, [f.name]: e.target.value })}
              placeholder={f.placeholder}
              className="mt-1 w-full rounded-md border border-border-tertiary bg-bg-primary px-3 py-1.5 text-sm focus:border-action focus:outline-none"
            />
          )}
        </label>
      ))}
      <button
        type="submit"
        disabled={saving}
        className="w-full rounded-md border border-action bg-action px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-action-fg transition disabled:opacity-50"
      >
        {saving ? 'saving…' : 'save medical info'}
      </button>
    </form>
  );
}

function BearSafetyCard({ doc }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="card overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-baseline justify-between border-b border-border-tertiary bg-bg-secondary px-4 py-3 text-left"
      >
        <span>
          <span className="block font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
            wildlife safety · cached
          </span>
          <span className="block font-serif text-base">{doc.title}</span>
        </span>
        <span className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
          {open ? 'hide' : 'show'}
        </span>
      </button>
      {open && (
        <div className="space-y-3 px-4 py-3 text-sm">
          {doc.summary && <p className="text-fg-primary">{doc.summary}</p>}
          {doc.rules?.length > 0 && (
            <ul className="space-y-1.5 text-fg-secondary">
              {doc.rules.map((r) => (
                <li key={r} className="flex gap-2">
                  <span className="text-fg-tertiary" aria-hidden>
                    •
                  </span>
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          )}
          {doc.recognize && (
            <div className="rounded-md bg-bg-secondary p-2 text-xs">
              <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
                recognize
              </p>
              <p className="mt-1 text-fg-primary">
                <span className="font-medium">Brown bear:</span> {doc.recognize.brown_bear}
              </p>
              <p className="mt-0.5 text-fg-primary">
                <span className="font-medium">Black bear:</span> {doc.recognize.black_bear}
              </p>
            </div>
          )}
          {doc.if_attacked && (
            <div className="rounded-md border border-danger/40 bg-danger/10 p-2 text-xs">
              <p className="font-mono text-[10px] uppercase tracking-wider text-danger">
                if attacked
              </p>
              <p className="mt-1 text-fg-primary">
                <span className="font-medium">Brown bear:</span> {doc.if_attacked.brown}
              </p>
              <p className="mt-0.5 text-fg-primary">
                <span className="font-medium">Black bear:</span> {doc.if_attacked.black}
              </p>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function fillTemplate(template, vars) {
  return template.replace(/\{(\w+)\}/g, (_, k) => {
    const v = vars[k];
    if (v == null) return '';
    return typeof v === 'number' ? v.toFixed(5) : String(v);
  });
}
